import { KazooError } from "@kazoohr/types";

import { assertIsTruthy } from "../assertIsTruthy";
import { coalesce } from "../coalesce";
import { DateTimeWrapper } from "../DateTimeWrapper";

import { SupportedDateFormat } from "./dateParsers";

/**
 * Accepts null, undefined, a date in the specified format or MM/DD and returns month and day
 */
export function parseMMDD({
  input,
  dateFormat,
  timezone,
  errorTranslationKey,
  errorTranslationValues,
}: {
  input?: string | null;
  dateFormat: SupportedDateFormat[];
  timezone: string;
  errorTranslationKey?: string;
  errorTranslationValues?: Record<string, unknown>;
}) {
  if (typeof input === "undefined") {
    return undefined;
  }

  if (input === null || !input.trim()) {
    return null;
  }

  const { month, day } = doInitialParse({
    input,
    dateFormat,
    timezone,
    errorTranslationKey: coalesce(
      errorTranslationKey,
      "packages.helpers.errors.invalidMonthDay"
    ),
    errorTranslationValues: coalesce(errorTranslationValues, {}),
  });

  assertIsValid({
    month,
    day,
    errorTranslationKey: coalesce(
      errorTranslationKey,
      "packages.helpers.errors.invalidMonthDay"
    ),
    errorTranslationValues: coalesce(errorTranslationValues, {}),
  });
  return { month, day };
}

function doInitialParse({
  input,
  dateFormat,
  timezone,
  errorTranslationKey,
  errorTranslationValues,
}: {
  input: string;
  dateFormat: SupportedDateFormat[];
  timezone: string;
  errorTranslationKey: string;
  errorTranslationValues: {};
}) {
  if (input.trim().length > 5) {
    try {
      const asDate = new DateTimeWrapper(input, dateFormat);
      asDate.applyTimeZone(timezone);

      return {
        month: asDate.localMonth(timezone) + 1, // yep.  js months are 0 indexed, but date of course isn't
        day: asDate.localDate(timezone),
      };
    } catch (error) {
      throw new KazooError(errorTranslationKey, {
        translationKey: errorTranslationKey,
        translationValues: errorTranslationValues,
        input,
      });
    }
  }

  const split = input.trim().split(/[/-]/);

  return {
    month: parseInt(split[0]),
    day: parseInt(split[1]),
  };
}

function assertIsValid({
  month,
  day,
  errorTranslationKey,
  errorTranslationValues,
}: {
  month: number;
  day: number;
  errorTranslationKey: string;
  errorTranslationValues: Record<string, unknown>;
}) {
  assertIsTruthy(isValidMonthDay({ month, day }), errorTranslationKey, {
    month,
    day,
    translationKey: errorTranslationKey,
    translationValues: errorTranslationValues,
  });
}

function isValidNumber(num: number) {
  if (isNaN(num)) {
    return false;
  }

  return num !== Infinity;
}

/* eslint-disable-next-line complexity */
function isValidMonthDay({ month, day }: { month: number; day: number }) {
  if (!isValidNumber(month) || !isValidNumber(day)) {
    return false;
  }

  if (month > 12 || month < 1) {
    return false;
  }

  if (day < 1) {
    return false;
  }

  if (month === 2) {
    return day < 30;
  }

  if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
    return day < 32;
  }

  return day < 31;
}
